import { format, getTime, formatDistanceToNow, isToday, isYesterday, startOfWeek, isAfter } from 'date-fns';

// ----------------------------------------------------------------------

export function fDate(date) {
  return format(new Date(date), 'dd MMMM yyyy');
}

export function fDateTime(date) {
  return format(new Date(date), 'dd MMM yyyy HH:mm');
}

export function fTimestamp(date) {
  return getTime(new Date(date));
}

export function fDateTimeSuffix(date) {
  return format(new Date(date), 'dd/MM/yyyy hh:mm p');
}

export function fToNow(date) {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
  });
}

/**
 * Formats a timestamp for chat display (e.g., "Today", "Yesterday", "Monday", "MM/DD/YYYY").
 * @param {number|string} timestamp - The timestamp in seconds or milliseconds.
 * @returns {string} The formatted date string.
 */
export function formatChatTimestamp(timestamp) {
  if (!timestamp || Number(timestamp) <= 0) return '';

  // Ensure timestamp is in milliseconds
  const ms = Number(timestamp) < 1e11 ? Number(timestamp) * 1000 : Number(timestamp);
  const date = new Date(ms);
  const now = new Date();

  if (isToday(date)) {
    return 'Today';
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday start
  if (isAfter(date, weekStart)) {
    return format(date, 'EEEE'); // e.g., "Monday"
  }

  return format(date, 'MM/dd/yyyy'); // e.g., "08/09/2023"
}

/**
 * Formats a time for chat display (e.g., "08:09 AM").
 * @param {number|string} timestamp - The timestamp in seconds or milliseconds.
 * @returns {string} The formatted time string.
 */
export function formatChatTime(timestamp) {
  if (!timestamp || Number(timestamp) <= 0) return '';
  const ms = Number(timestamp) < 1e11 ? Number(timestamp) * 1000 : Number(timestamp);
  const date = new Date(ms);
  return format(date, 'hh:mm a'); // e.g., "08:09 AM"
}

/**
 * Formats a timestamp to a combined US-style date and time string (e.g., "Today 08:09 AM", "Yesterday 03:30 PM", "08/09/2023 11:45 AM").
 * @param {number|string} timestamp - The timestamp in seconds or milliseconds.
 * @returns {string} The formatted date and time string.
 */
export function formatUsDateTime(timestamp) {
  if (!timestamp || Number(timestamp) <= 0) return '';
  const datePart = formatChatTimestamp(timestamp);
  const timePart = formatChatTime(timestamp);
  return `${datePart} ${timePart}`;
}

/**
 * Formats a timestamp for chat list display based on specific rules.
 * 1. If the message is from today, show only the time (e.g., "12:00 PM").
 * 2. If the message is from yesterday, show "Yesterday".
 * 3. If the message is older than yesterday, show the full US-formatted date (MM-DD-YYYY).
 * @param {number|string} timestamp - The timestamp in seconds or milliseconds.
 * @returns {string} The formatted time string.
 */
export function formatChatListTime(timestamp) {
  if (!timestamp || Number(timestamp) <= 0) return '';

  // Ensure timestamp is in milliseconds
  const ms = Number(timestamp) < 1e11 ? Number(timestamp) * 1000 : Number(timestamp);
  const date = new Date(ms);
  const now = new Date();

  if (isToday(date)) {
    return format(date, 'hh:mm a'); // e.g., "12:00 PM"
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  return format(date, 'MM-dd-yyyy'); // e.g., "11-26-2025"
}